import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Icono } from '@xiltrion/ui/icono';
import { Tooltip, TooltipContent, TooltipTrigger } from '@xiltrion/ui/tooltip';
import { apiDelete, apiGet, apiPut } from '@/api/client';
import { notify } from './lib';

const favoritesKey = ['favorites'] as const;
const idsKey = [...favoritesKey, 'ids'] as const;
type FavoritesView = { caseIds: string[]; markedAt: Record<string, string> };

const useFavoritesQuery = () => useQuery({ queryKey: idsKey, queryFn: () => apiGet<FavoritesView>('/favorites'), staleTime: 5 * 60_000 });

/** Los radicados favoritos de quien está en sesión (una sola consulta para todos los listados). */
export function useFavorites() {
  const data = useFavoritesQuery().data;
  return useMemo(() => new Set(data?.caseIds ?? []), [data]);
}

/** Cuándo marcó cada favorito (columna «Marcado» del Archivo). */
export function useFavoriteDates(): Record<string, string> {
  return useFavoritesQuery().data?.markedAt ?? {};
}

/** Marcar o quitar: la estrella cambia al instante y vuelve atrás si el API no lo acepta. */
function useToggleFavorite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ caseId, favorite }: { caseId: string; favorite: boolean }) => (favorite ? apiPut(`/favorites/${caseId}`) : apiDelete(`/favorites/${caseId}`)),
    onMutate: async ({ caseId, favorite }) => {
      await qc.cancelQueries({ queryKey: idsKey });
      const before = qc.getQueryData<FavoritesView>(idsKey);
      const ids = new Set(before?.caseIds ?? []);
      if (favorite) ids.add(caseId); else ids.delete(caseId);
      const markedAt = { ...(before?.markedAt ?? {}) };
      if (favorite) markedAt[caseId] = new Date().toISOString(); else delete markedAt[caseId];
      qc.setQueryData<FavoritesView>(idsKey, { caseIds: [...ids], markedAt });
      return { before };
    },
    onError: (_e, _v, ctx) => { qc.setQueryData(idsKey, ctx?.before); notify('error', 'No pudimos guardar el favorito', 'Intenta de nuevo.'); },
    // El Archivo con «Mis favoritos» se filtra en el API: se vuelve a pedir.
    onSettled: () => { void qc.invalidateQueries({ queryKey: favoritesKey }); void qc.invalidateQueries({ queryKey: ['cases', 'list'], predicate: (q) => (q.queryKey[2] as { favorites?: boolean } | undefined)?.favorites === true }); },
  });
}

/**
 * Estrella de favorito de un radicado, para listados y para el detalle. Los favoritos son de cada persona. Es solo la
 * estrella, sin botón, centrada en la fila: vacía en gris atenuado al 25 % de opacidad; al pasar el ratón se rellena de amarillo al 75 %; marcada, amarilla del todo. En una
 * fila clicable no abre el expediente al tocarla.
 */
export function FavoriteStar({ caseId, label, size = 'icon-sm' }: { caseId: string; label: string; size?: 'icon-sm' | 'icon' }) {
  const favorites = useFavorites();
  const toggle = useToggleFavorite();
  const on = favorites.has(caseId);
  const text = on ? `Quitar ${label} de favoritos` : `Marcar ${label} como favorito`;
  const dim = size === 'icon' ? 'size-6' : 'size-5';
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span role="button" tabIndex={0} aria-pressed={on} aria-label={text}
          className={`group relative flex ${dim} shrink-0 cursor-pointer rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring`}
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); toggle.mutate({ caseId, favorite: !on }); }}
          onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle.mutate({ caseId, favorite: !on }); } }}>
          {/* Las dos estrellas están siempre dibujadas, una sobre otra; solo cambia la opacidad. Si se cambiara de ícono al
              pasar el ratón, la llena tardaría un instante en aparecer la primera vez y parpadearía. */}
          <Icono nombre="bi:star" className={`absolute inset-0 size-full text-muted-foreground transition-opacity ${on ? 'opacity-0' : 'opacity-25 group-hover:opacity-0'}`} />
          <Icono nombre="bi:star-fill" className={`absolute inset-0 size-full text-favorito transition-opacity ${on ? 'opacity-100' : 'opacity-0 group-hover:opacity-75'}`} />
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom">{on ? 'Quitar de favoritos' : 'Marcar como favorito'}</TooltipContent>
    </Tooltip>
  );
}
